/**
 * ContractsTab — extracted from the 3k+ line `CompanyManagerDashboard.js`.
 * First tab-per-file refactor step. Same functionality, same testids, no
 * behavior change — just isolated so the parent file shrinks and the
 * tab can be unit-tested independently.
 */
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractsTab({
  contractForm,
  setContractForm,
  handleContractUpload,
  uploadingContract,
  properties,
  contracts,
}) {
  return (
    <div className="space-y-4">
      <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-6" data-testid="contracts-upload-card">
        <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          UPLOAD CONTRACTS
        </h2>
        <p className="text-sm text-zinc-400 mb-4">Upload PDF, DOCX, or image contracts for each property.</p>
        <form onSubmit={handleContractUpload} className="space-y-4">
          <div>
            <Label>Property *</Label>
            <Select
              value={contractForm.property_id}
              onValueChange={(value) => setContractForm({ ...contractForm, property_id: value })}
            >
              <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="contract-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {properties.map((property) => (
                  <SelectItem key={property.property_id} value={property.property_id}>{property.property_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Contract File *</Label>
            <Input
              type="file"
              accept=".pdf,.docx,image/*"
              onChange={(e) => setContractForm({ ...contractForm, file: e.target.files?.[0] || null })}
              className="bg-zinc-800 border-zinc-700"
              data-testid="contract-file-input"
            />
          </div>
          <Button
            type="submit"
            className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
            disabled={uploadingContract}
            data-testid="contract-upload-submit"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploadingContract ? 'Uploading...' : 'Upload Contract'}
          </Button>
        </form>
      </Card>

      <Card className="bg-zinc-900 border-zinc-700 p-6" data-testid="contracts-list-card">
        <h3 className="text-lg font-bold text-white mb-4">Stored Contracts</h3>
        {contracts.length === 0 ? (
          <p className="text-sm text-zinc-400" data-testid="contracts-empty">No contracts uploaded yet</p>
        ) : (
          <div className="space-y-3">
            {contracts.map((contract) => (
              <div key={contract.contract_id} className="flex flex-wrap items-center justify-between gap-3 border border-zinc-700 rounded-lg p-4" data-testid={`contract-row-${contract.contract_id}`}>
                <div>
                  <p className="text-white font-semibold">{contract.property_name}</p>
                  <p className="text-xs text-zinc-400">{contract.original_filename}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {contract.region_name && (
                      <Badge className="bg-[#FFD60A] text-black text-[10px] uppercase" data-testid={`contract-region-${contract.contract_id}`}>
                        {contract.region_name}
                      </Badge>
                    )}
                    {contract.city && (
                      <span className="text-[11px] text-zinc-400">{contract.city}{contract.state ? `, ${contract.state}` : ''}</span>
                    )}
                    <span className="text-xs text-zinc-500">Uploaded {new Date(contract.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="border-[#FFD60A] text-[#FFD60A]"
                  onClick={() => window.open(`${BACKEND_URL}/api/contracts/${contract.contract_id}/download`, '_blank')}
                  data-testid={`contract-download-${contract.contract_id}`}
                >
                  Download
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
